/* eslint-disable func-names */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { matchPath } from "react-router";
import { PDFViewer } from "@react-pdf/renderer";
import html2canvas from "html2canvas";

import Report from "./PDF_Renderer/Report";
import Map from "./Components/Map";
import WordCloud from "../Audience/CreateWithoutTemplate/AudienceDescription/WordCloud";
import Api from "../../api";
import { fullReportPdf } from "../../router/routes";
import styles from "./style.module.scss";

class PdfRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mapImage: null,
      tagImage: null,
      pdfRenderd: false,
      preloadedPersonaDescription: []
    };
    // tagRef = React ref to wrapper of wordcloud module
    this.tagRef = null;
    this.setTagImage = this.setTagImage.bind(this);
  }

  async componentDidMount() {
    await this.fetchData();
  }

  shouldComponentUpdate() {
    return !this.state.pdfRenderd;
  }

  async setTagImage() {
    const canvas = await html2canvas(this.tagRef);
    const tagImage = canvas.toDataURL("image/png");
    return this.setState({ tagImage });
  }

  async fetchData() {
    const { location } = this.props;
    const matched = matchPath(location.pathname, {
      path: fullReportPdf(),
      exact: false,
      strict: false
    });
    const { threshold, id } = matched.params;

    await Api.audience.stats({
      data: { audienceId: id, threshold }
    });

    await Api.audience.demographics({
      params: { audience_id: id }
    });

    await Api.audience.map({
      data: { audienceId: id, threshold }
    });

    await Api.audience.tags({
      data: { audienceId: id, threshold }
    });

    await Api.audience.personaDescription({
      data: { audienceId: id, threshold }
    });

    const pesonaDescriptionWithImages = [];

    const preloadAllImages = async () => {
      return Promise.all(
        this.props.personaDescription.map(async persona => {
          const preloadImage = async () => {
            return new Promise((resolve, reject) => {
              fetch(persona.imageLink)
                .then(response => response.blob())
                .then(blob => {
                  const reader = new FileReader();
                  reader.onload = function() {
                    resolve(this.result);
                  }; // <--- `this.result` contains a base64 data URI
                  reader.onerror = function() {
                    reject(new Error(`Error loading ${persona.imageLink}`));
                  }; // <---
                  reader.readAsDataURL(blob);
                });
            });
          };
          try {
            const preloadedImage = await preloadImage();

            pesonaDescriptionWithImages.push({
              ...persona,
              img: preloadedImage
            });
          } catch (error) {
            console.error(error);
          }
        })
      );
    };

    await preloadAllImages();

    this.setState({ preloadedPersonaDescription: pesonaDescriptionWithImages });
  }

  render() {
    const { tags, stats, mapData, demographics } = this.props;
    const {
      mapImage,
      tagImage,
      pdfRenderd,
      preloadedPersonaDescription
    } = this.state;
    const countryName = stats?.country?.name;
    if (this.tagRef && !tagImage) {
      this.setTagImage();
    }
    const dataForPdfNotReady =
      !tags.length ||
      !mapData.length ||
      !demographics.length ||
      !Object.keys(stats).length ||
      !preloadedPersonaDescription.length ||
      !mapImage ||
      !tagImage;

    return dataForPdfNotReady ? (
      <>
        <div className={styles.preprocess}>
          <div className={styles["loading-container"]}>
            <h1 className="title">Loading....</h1>
          </div>
          {!!mapData.length && (
            // TODO: HIGHLIGHT: Need to render real map component to get image copy of it
            <Map
              getCanvas={can => {
                this.setState({ mapImage: can.toDataURL("image/png") });
              }}
              postalCodes={mapData}
              country={countryName?.toLowerCase()}
              center={[
                stats?.country?.mapboxCenterLat,
                stats?.country?.mapboxCenterLon
              ]}
              zoom={stats?.country?.mapboxDefaultZoom}
              sourceLayer={stats?.country?.mapboxSourceLayer}
              url={`mapbox://${stats?.country?.mapboxTilesetId}`}
            />
          )}
          {!!tags.length && (
            <div
              ref={ref => {
                this.tagRef = ref;
              }}
              className={styles.wordcloud}
            >
              <WordCloud words={tags} />
            </div>
          )}
        </div>
      </>
    ) : (
      <>
        {!pdfRenderd ? (
          <>{this.setState({ pdfRenderd: true })}</>
        ) : (
          <PDFViewer
            style={{
              width: "100%",
              height: "100vh",
              margin: "0",
              border: "none"
            }}
          >
            <Report
              data={{
                mapImage,
                tagImage,
                personaDescription: preloadedPersonaDescription,
                stats,
                demographics
              }}
            />
          </PDFViewer>
        )}
      </>
    );
  }
}

PdfRenderer.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  personaDescription: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stats: PropTypes.shape({
    country: PropTypes.shape({
      name: PropTypes.string,
      mapboxCenterLat: PropTypes.number,
      mapboxDefaultZoom: PropTypes.number,
      mapboxSourceLayer: PropTypes.string,
      mapboxTilesetId: PropTypes.string,
      mapboxCenterLon: PropTypes.number
    })
  }).isRequired,
  mapData: PropTypes.arrayOf(PropTypes.string).isRequired,
  demographics: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

const mapStateToProps = ({ audiences }) => {
  return {
    tags: audiences.tags,
    stats: audiences.stats,
    personaDescription: audiences.description,
    mapData: audiences.map,
    demographics: audiences.demographics
  };
};

export default connect(mapStateToProps, null)(PdfRenderer);
